export const EFFECTIVE_DATE = 'August 5, 2026';
export const LAST_UPDATED = 'August 5, 2026';

export const INTRO =
  'Nothing To Wear is a peer-to-peer marketplace connecting people who lend or sell fashion items ("Lenders" and "Sellers") with people who rent or buy them ("Renters" and "Buyers"). This Lender & Seller Policy explains what you can expect from us, what’s expected of you, how you get paid, and how liability is handled when you list an item. It supplements — and should be read alongside — our Terms and Conditions and Privacy Policy, and forms part of the agreement you accept when you use the Platform. Capitalized terms not defined here have the meaning given in our Terms and Conditions.';

export const SECTIONS = [
  {
    number: '1',
    title: 'Who This Policy Covers',
    blocks: [
      {
        p:
          'This Policy applies to you whenever you list, lend, or sell an item through the Platform. A separate Renter & Buyer Policy applies to you when you are the one renting or buying.',
      },
    ],
  },
  {
    number: '2',
    title: 'Eligibility',
    blocks: [
      {
        p:
          'To list an item, you must be at least 18 years old, have reached the age of majority in your province or territory of residence, own the item or otherwise have the legal right to lend or sell it, and complete our identity verification and Stripe Connect onboarding described in Section 3 before you can receive a payout.',
      },
    ],
  },
  {
    number: '3',
    title: 'Getting Paid: Stripe Connect Onboarding',
    blocks: [
      {
        p:
          "We use Stripe Connect to pay Lenders and Sellers directly. Before your first Listing can go live (or before your first payout, depending on how we configure onboarding), you'll be asked to complete Stripe's own onboarding flow, which collects:",
      },
      {
        list: [
          'identity information (name, date of birth, address, and a government-issued ID number or similar), which Stripe uses to meet Know Your Customer (KYC) requirements imposed on all payment processors; and',
          'your Canadian bank account details, so Stripe can deposit your payouts directly.',
        ],
      },
      {
        p:
          "Stripe — not us — collects and verifies this information, and you'll also be agreeing to Stripe's own connected-account terms as part of that flow. We never see or store your full banking or government ID details; we only receive confirmation from Stripe that your account is verified and able to receive payouts. You can view your payout history and update your banking details at any time through your Stripe-hosted dashboard, linked from your account settings.",
      },
    ],
  },
  {
    number: '4',
    title: 'Creating a Listing: Your Responsibilities',
    blocks: [
      { p: 'When you list an item, you are making these promises to us and to whoever rents or buys it:' },
      {
        list: [
          'you own the item, or otherwise have the right to lend or sell it, free of any undisclosed lien, claim, or third-party interest;',
          "it's authentic, legally yours to offer, and not stolen or counterfeit;",
          'your photos, description, sizing, and condition notes are accurate and current, including flaws, alterations, odours, or repairs; and',
          "it complies with applicable Canadian product-safety and labelling rules, and isn't on our prohibited-items list.",
        ],
      },
      {
        p:
          'You control your own pricing and availability calendar. We may decline to publish, or may remove, a Listing that appears inaccurate, unsafe, prohibited, or otherwise inconsistent with our Terms, and will try to tell you why.',
      },
    ],
  },
  {
    number: '5',
    title: 'Commission, Fees, and How You Get Paid',
    blocks: [
      { h3: '5.1 Our Commission' },
      {
        boldLead:
          'We charge a flat 20% commission on the gross rental fee or sale price of every completed Transaction.',
        p:
          " This is deducted automatically by Stripe at the time of payment — you don't need to remit anything yourself. You keep 80% of the price the Renter or Buyer pays for the item, before taxes; taxes and the Renter-side Buyer Protection Fee are handled separately and don't affect your 80%.",
      },
      { h3: '5.2 Worked Example' },
      { p: 'If a dress rents for CAD $100 for a weekend:' },
      {
        callout: [
          { label: 'Renter pays', value: '$100 + a 5% Buyer Protection Fee ($5), plus applicable tax.' },
          { label: 'Platform commission (20%)', value: '$20.' },
          { label: 'Your payout', value: '$80.' },
        ],
      },
      { h3: '5.3 Payout Timing' },
      {
        p:
          'For rentals: when a Renter books, their payment is charged and held in our Stripe platform account. Your earnings stay held with Stripe until the Transaction is marked complete. Completion happens automatically 2 days after the Rental Period ends, or we may mark it complete earlier at our discretion. Once the Transaction is complete, Stripe begins sending your payout to your bank account, which typically takes 5–10 business days to arrive. For sales, your payout is released once delivery is confirmed and the Buyer’s claim window (Section 10 of the Renter & Buyer Policy) has closed without an open dispute — generally within 3 business days, plus Stripe’s own bank transfer time to reach your account.',
      },
      { h3: '5.4 Reserves and Holds' },
      {
        p:
          'We may briefly hold or delay a specific payout where a damage claim, chargeback, or fraud review is in progress for that Transaction, and will let you know why.',
      },
    ],
  },
  {
    number: '6',
    title: 'Your Taxes, and What We Report to the CRA',
    blocks: [
      { h3: '6.1 Your Own Tax Obligations' },
      {
        p:
          'Income you earn from renting or selling items is generally taxable, and you are responsible for reporting it to the Canada Revenue Agency (CRA), typically as business or rental income. If your worldwide taxable sales (including sales made off the Platform) exceed CAD $30,000 over any 12-month period, you may be required to register for, collect, and remit GST/HST yourself, separate from anything we do at the platform level. We are not able to give you tax advice — please speak with a qualified accountant about your specific situation.',
      },
      { h3: '6.2 Information We Collect and Report on Sales' },
      {
        p:
          'Under Canada’s Reporting Rules for Digital Platform Operators (Part XX of the Income Tax Act), we are generally required to collect your tax identification number (your SIN, or your business number if you sell through a registered business) and report certain information about your item sales to the CRA annually, unless you qualify as an excluded seller for that calendar year — meaning you had fewer than 30 reportable sale transactions and total sale proceeds of CAD $2,800 or less through the Platform that year. If you cross either threshold, all of your sale transactions for that year become reportable, not just the ones after the threshold was crossed.',
      },
      {
        p:
          "If you don't provide your tax identification number when we ask for it, the CRA can assess a penalty of $500 against you directly, and we may need to restrict payouts until it's provided. Where we do report your information, we'll send you a copy of what was reported by January 31 of the following year, matching what's sent to the CRA.",
      },
      { h3: '6.3 Rentals Are Treated Differently, for Now' },
      {
        p:
          'As currently written, the federal reporting rules cover the sale of goods, personal services, and the rental of real property or vehicles — they do not currently list the rental of goods like clothing or accessories as a separately reportable activity. That means your rental (as opposed to sale) transactions on the Platform are not expected to trigger this specific CRA reporting requirement today. This is a developing area of tax law, and the scope of "relevant activities" could be expanded by future regulation; we\'ll update this Policy if that happens.',
      },
    ],
  },
  {
    number: '7',
    title: 'Rental Listings: Specific Responsibilities',
    blocks: [
      {
        list: [
          'dispatch the item clean, in the condition shown in your photos, in time for the agreed delivery or pickup;',
          'clearly note any care requirements (e.g., dry clean only, no ironing) in your Listing;',
          'respond to booking requests and Renter messages in a timely manner; and',
          "inspect a returned item the day it's returned and, if it's damaged, email help@nothingtowearclub.ca that same day with photos (see Section 9), since your payout timing depends on it.",
        ],
      },
    ],
  },
  {
    number: '8',
    title: 'Sale Listings: Specific Responsibilities',
    blocks: [
      {
        list: [
          "accurately grade the item's condition and disclose material flaws you're aware of;",
          'ship or hand off the item within the timeframe stated in your Listing; and',
          'respond promptly to a "not as described" claim raised under Section 10 of the Renter & Buyer Policy, including by providing your own evidence (e.g., original listing photos, proof of shipment).',
        ],
      },
    ],
  },
  {
    number: '9',
    title: 'Check-In and Check-Out: Reporting Late Returns, Loss, and Damage',
    blocks: [
      {
        p:
          "For your protection and the Renter's, we strongly recommend that you fully inspect the item and take photos when you hand it off (check-out) and again when it's returned (check-in), and upload those photos to the in-app messaging thread for that booking. Try to resolve any disagreement about an item's condition directly with the Renter through in-app messaging first.",
      },
      {
        list: [
          'Item returned late? Email help@nothingtowearclub.ca on the date the item was scheduled to be returned.',
          "Item returned damaged? Email help@nothingtowearclub.ca the same day it's returned, with photos (and, where you have them, photos from before the rental for comparison).",
        ],
      },
      {
        p:
          "If you can't resolve it directly with the Renter, we'll review the evidence from both sides. Where a claim is approved, we recover the approved amount from the Renter's payment method and pass it to you as an additional payout, up to the liability cap set out in the Renter & Buyer Policy.",
      },
      {
        p:
          "Late Return Payouts: if a Renter returns an item late, you'll receive an additional payout equal to 150% of the item's daily rental rate for each calendar day the item is late, calculated the same way as the Renter's late fee under the Renter & Buyer Policy (Section 9). This payout stops accruing once the Renter's total late fee reaches its maximum under that Policy.",
      },
    ],
  },
  {
    number: '10',
    title: 'Cancelling a Listing or a Confirmed Booking',
    blocks: [
      {
        p:
          "We know plans change. If you need to cancel an item — whether pulling a Listing or cancelling a confirmed booking — please give at least 14 days' notice before any future booking dates on that item. That gives a Renter with an upcoming booking time to find another outfit for their event, which matters in a peer-to-peer community like ours, where we're all counting on each other.",
      },
      {
        p:
          "There's no penalty for cancelling in good faith with reasonable notice. If a booking starting sooner than 14 days needs to be cancelled, contact us at help@nothingtowearclub.ca as soon as possible so we can help make it right for the Renter; in that case, they receive a full refund and you do not receive a payout for that Transaction.",
      },
    ],
  },
  {
    number: '11',
    title: "Conduct We Don't Allow",
    blocks: [
      {
        list: [
          'arranging payment or exchange outside the Platform to avoid the commission or verification process;',
          "listing an item you don't own or can't legally lend or sell;",
          'posting misleading photos, descriptions, or reviews;',
          "refusing to transact with a Renter/Buyer based on a protected characteristic under BC's Human Rights Code; and",
          'pressuring a Renter/Buyer to alter a review or damage claim.',
        ],
      },
    ],
  },
  {
    number: '12',
    title: 'Listing Removal and Account Suspension',
    blocks: [
      {
        p:
          "We may remove a Listing or suspend your account where we reasonably believe you've breached this Policy, our Terms, or applicable law, or where continuing to list poses a safety or fraud risk, and will make reasonable efforts to tell you why. See our Terms and Conditions, Section 19, for the general suspension and termination framework.",
      },
    ],
  },
  {
    number: '13',
    title: 'Your Liability as the Party Warranting the Item',
    blocks: [
      {
        p:
          "Because you're the one who owns, describes, and warrants the item, you're responsible for the accuracy of your Listing and for any claim arising from the item itself being unsafe, counterfeit, stolen, or materially different from its description. You agree to indemnify us for losses we incur as a direct result of a breach of the warranties in Section 4, except to the extent caused by our own negligence — consistent with the indemnification terms in our Terms and Conditions.",
      },
    ],
  },
  {
    number: '14',
    title: 'Insurance',
    blocks: [
      {
        p:
          "The Buyer Protection Fee charged to Renters (see the Renter & Buyer Policy) is described in full, including its limits, at checkout, and does not make us your insurer or replace your own coverage. If you lend or sell high-value items regularly, consider whether your personal property or business insurance covers items while they're in a Renter's or Buyer's possession, since our damage-claim process is not a substitute for insurance.",
      },
    ],
  },
  {
    number: '15',
    title: 'Our Liability to You',
    blocks: [
      {
        p:
          'Nothing To Wear is a marketplace, not a party to your rental or sale contract with the Renter/Buyer. To the fullest extent permitted by law, and except for a right that cannot be limited under the BPCPA or other applicable law, our total liability to you for a claim relating to a specific Transaction is capped at the commission we collected on that Transaction.',
      },
    ],
  },
  {
    number: '16',
    title: "Chargebacks and Payment Disputes After You've Been Paid",
    blocks: [
      {
        p:
          "If a Renter or Buyer successfully disputes a charge with their card issuer after you've already received a payout for that Transaction, Stripe may reverse the funds. Where that happens, we may recover the disputed amount from a future payout or your Stripe balance. We'll notify you and give you the opportunity to submit evidence to contest the dispute through Stripe wherever that option is available.",
      },
    ],
  },
  {
    number: '17',
    title: 'When You Might Be Treated as a Business, Not a Casual Seller',
    blocks: [
      {
        p:
          'Most Lenders and Sellers use the Platform casually, alongside their regular life — that\'s the peer-to-peer model this Policy is written for. But if you list and transact at high volume or frequency, Canadian and BC law may start treating you as "carrying on a business," which can carry its own consequences separate from anything we require: possible GST/HST or PST registration obligations (Section 6.1), potential business-licensing requirements in your municipality, and, if you are found to be routinely acting as a commercial supplier rather than a peer, you personally could become subject to consumer-protection obligations toward the people who rent or buy from you. This is a legal and tax question specific to your own activity, not something we determine on your behalf — talk to an accountant or lawyer if your activity on the Platform is substantial or growing.',
      },
    ],
  },
  {
    number: '18',
    title: 'Dispute Resolution',
    blocks: [
      {
        p:
          "Most issues between you and a Renter/Buyer should be raised through the Platform's resolution tools first. For a dispute with us that can't be resolved informally, you can bring it to the BC Civil Resolution Tribunal or the applicable BC court; nothing in our Terms requires arbitration of a claim of $5,000 or less, or bars you from a class proceeding for such a claim. See our Terms and Conditions, Section 20, for full detail.",
      },
    ],
  },
  {
    number: '19',
    title: 'Privacy',
    blocks: [
      {
        p:
          "Information about how we collect, use, and share your personal information — including what we're required to collect for Stripe onboarding and CRA reporting — is set out in our Privacy Policy.",
      },
    ],
  },
  {
    number: '20',
    title: 'Changes to This Policy',
    blocks: [
      {
        p:
          "We may update this Policy from time to time, including to reflect changes in Stripe's requirements or in tax law. Material changes will be communicated in advance, consistent with the approach described in our Terms and Conditions.",
      },
    ],
  },
];

export const CONTACT = {
  number: '21',
  title: 'Contact Us',
  companyLine: 'Nothing To Wear Club Inc. (operating as "Nothing To Wear")',
  email: 'help@nothingtowearclub.ca',
  address: 'Unit #30, 5300 Main St, Kelowna, BC V1W 3W4',
};
